import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { type UseQueryResult } from '@tanstack/react-query'
import { type AutocompleteRequestUsageEnum, type AutocompleteResult } from 'invictus-sdk-typescript'

import { type AutocompleteResultModel } from '@DS/components/navigation/search/atoms/AutocompleteResult'
import { type AutocompleteSection } from '@DS/components/navigation/search/autocomplete/Autocomplete.types'

import { type Autocomplete, type AutocompleteOptions, useAutocomplete } from './useAutocomplete'
import { type SearchAutocompleteQueryOptions, useSearchAutocompleteQuery } from './useSearchAutocompleteQuery'

export const PLACES_AREA = 'places_area'

export type SearchAutocompleteOptions = Omit<
  AutocompleteOptions<AutocompleteResultModel>,
  'cancelGetAutocompleteItems'
> & {
  hasToKeepStationsOnly?: boolean
  usage?: AutocompleteRequestUsageEnum
  shouldReturnSuggestions?: boolean
}

export const useSearchAutocomplete: (
  suggestions: AutocompleteSection<AutocompleteResultModel>[],
  options?: SearchAutocompleteOptions,
) => Autocomplete<AutocompleteResultModel> = (
  suggestions,
  { hasToKeepStationsOnly = false, usage, ...restOptions } = {},
) => {
  const { formatMessage } = useIntl()

  const mapAutocompleteGroupToAutocompleteSections = useCallback(
    ({
      places: { transportPlaces, addresses, pois },
    }: AutocompleteResult): AutocompleteSection<AutocompleteResultModel>[] => [
      { area: PLACES_AREA, title: formatMessage({ id: 'citiesAndStations' }), items: transportPlaces },
      { area: PLACES_AREA, title: formatMessage({ id: 'addresses' }), items: addresses },
      { area: PLACES_AREA, title: formatMessage({ id: 'pois' }), items: pois },
    ],
    [formatMessage],
  )

  const useWrapSearchAutocompleteQuery = (
    inputValue: string,
    options?: SearchAutocompleteQueryOptions<AutocompleteSection<AutocompleteResultModel>[]>,
  ): UseQueryResult<AutocompleteSection<AutocompleteResultModel>[]> => {
    const overriddenOptions = useMemo(
      () => ({
        ...options,
        select: (data: AutocompleteResult) => mapAutocompleteGroupToAutocompleteSections(data),
      }),
      [options],
    )

    return useSearchAutocompleteQuery(inputValue, overriddenOptions, {
      keepStationsOnly: hasToKeepStationsOnly,
      usage,
    })
  }

  return useAutocomplete(useWrapSearchAutocompleteQuery, suggestions, restOptions)
}
